import { Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { isNullOrUndefined } from '@pineapplelab/util';
import { Dimensions, ImageCroppedEvent, ImageCropperComponent } from 'ngx-image-cropper';
import { ToasterService } from '@app/services/toaster/toaster.service';

@Component({
    selector: 'app-upload-image',
    templateUrl: './upload-image.component.html',
    styleUrls: ['./upload-image.component.scss'],
    standalone: false
})
export class UploadImageComponent implements OnInit {
  @Input() url: string;
  @Input() hiddenDelete: boolean = false;
  @Input() label: string;
  @Input() recommendedSizeKey: string;
  @Input() rounded = false;
  @Input() hiddenPreview = false;
  @Input() freeCropper = false;
  @Input() previewPosition: 'top' | 'top-right' | 'bottom' | 'bottom-right' | 'right' | 'low' | 'low-right' = 'top-right';
  @Input() aspectRatio = 1 / 1;
  @Input() model: any = null;
  @Input() onlyImg: boolean = false;
  @Input() isIconImage: boolean = true;
  @Input() downloadBtn: boolean = true;
  @Input() localUrl: string;
  @Output() fileChange = new EventEmitter<File>();
  @Input() fitMode: 'original' | 'cover' = 'cover';
  @ViewChild('cropper') cropperModal: TemplateRef<any>;
  @ViewChild(ImageCropperComponent) imageCropperRef?: ImageCropperComponent;

  imageChangedEvent: Event = null;
  imageFile: File = null;
  lastFileName: string = null;
  isDraggingOver = false;
  // Holds the latest cropped result while the crop dialog is open. `fileChange` is only
  // emitted once the user confirms via the "Save" button (see `closeModal()`), not on every
  // intermediate crop adjustment — otherwise consumers reacting to `fileChange` (e.g. DNI
  // auto-extraction in `edit-profile.component.ts`) would fire repeatedly before the user
  // has finished cropping/confirmed anything.
  private pendingCroppedFile: File = null;
  // Object URL for the latest cropped result. Held here (not assigned to `localUrl`) until the
  // user confirms with "Save", so the MAIN widget's `@if ((url || localUrl) ...)` isn't flipped
  // mid-tick by the cropper's automatic on-load `(imageCropped)` emission (which would throw
  // NG0100 — conditional branch index changing from hidden to shown during checkNoChanges).
  private pendingCroppedUrl: string = null;

  dialogReference: any;

  constructor(private toasterService: ToasterService, private dialog: MatDialog, private translateService: TranslateService) {}

  ngOnInit(): void {}

  removeImage() {
    this.localUrl = null;
    this.url = null;
    this.imageChangedEvent = null;
    this.imageFile = null;
    this.pendingCroppedFile = null;
    this.pendingCroppedUrl = null;
    this.fileChange.emit(null);
    if (!isNullOrUndefined(this.dialogReference)) {
      this.dialogReference.close(true);
    }
  }

  fileChangeEvent(event: Event): void {
    const files = (event as any).target.files as FileList;
    if (!this.validateAndPrepareFile(files.length > 0 ? files[0] : null)) {
      return;
    }
    this.imageFile = null;
    this.imageChangedEvent = event;
    this.openCropperDialog();
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingOver = false;
    const file = event.dataTransfer?.files?.length > 0 ? event.dataTransfer.files[0] : null;
    if (!this.validateAndPrepareFile(file)) {
      return;
    }
    this.imageChangedEvent = null;
    this.imageFile = file;
    this.openCropperDialog();
  }

  /**
   * `<image-cropper>` sizes its clear/crop area (`.ngx-ic-overlay` + `.ngx-ic-cropper`) from a
   * single internal `maxSize`, measured ONCE — `getComputedStyle(sourceImage).width` — the moment
   * its source image first reports a layout width. It has no ResizeObserver, so if that first
   * measurement happens while the `MatDialog` panel is still mid enter-animation (its width still
   * transitioning), `maxSize` locks to a too-small width and the overlay masks part of the image
   * forever (its `outline: ... solid 100vw` trick) — looking like "the overlay isn't 100% wide".
   *
   * The fix is deterministic: open the dialog with a fixed, viewport-relative size AND with the
   * enter animation disabled (`enterAnimationDuration: '0ms'`), so the panel is already at its
   * final size on the very first frame. The cropper then measures the correct width on its first
   * pass and no post-open "resync" is needed. (The previous approach — dispatching a synthetic
   * `window:resize` after open — also caused an `NG0100 ExpressionChangedAfterItHasBeenChecked`,
   * because the cropper is hosted via a `TemplateRef` inside this component's change-detection
   * tree, so forcing it to recompute geometry after it had been checked tripped Angular's
   * dev-mode double-check.)
   */
  private openCropperDialog(): void {
    this.cropperResizeRetries = 0;
    this.dialogReference = this.dialog.open(this.cropperModal, {
      width: '90vw',
      maxWidth: '1100px',
      panelClass: 'image-cropper-dialog',
      // No enter animation: the panel is at its final size from the first frame, so the cropper's
      // one-shot width measurement is correct and the overlay covers the whole image.
      enterAnimationDuration: '0ms',
    });
  }

  private cropperResizeRetries = 0;

  onCropperReady(size: Dimensions): void {
    if (size && size.width > 1 && size.height > 1) {
      this.cropperResizeRetries = 0;
      return;
    }
    if (this.cropperResizeRetries >= 5) {
      return;
    }
    this.cropperResizeRetries++;
    requestAnimationFrame(() => this.imageCropperRef?.onResize());
  }

  private validateAndPrepareFile(file: File): boolean {
    const maxSizeInBytes = 5 * 1024 * 1024; // 5 MB

    if (file) {
      const validFileTypes = ['image/jpg', 'image/jpeg', 'image/png', 'image/svg+xml'];
      if (file.size > maxSizeInBytes) {
        this.toasterService.show(this.translateService.instant('langs.toaster_file_size_exceeds') + `of ${maxSizeInBytes}`, 'Error'); // this message error its supossed to show in the UI
        return false;
      }
      if (!validFileTypes.includes(file.type)) {
        this.toasterService.show(this.translateService.instant('langs.toaster_wrong_image_format'), 'Error');
        return false;
      }
    }

    if (file) {
      this.lastFileName = file.name;
      // `model` can be undefined when the caller binds it via optional chaining on a
      // not-yet-initialized nested object (e.g. `[model]="data.card?.image"`) — don't crash.
      if (this.model) {
        this.model.fileName = this.lastFileName;
        let fileSizeInMegabytes = file.size / Math.pow(1024, 1);
        const unit = 'KB';
        const result = this.isSizeInMegabytes(fileSizeInMegabytes, unit);
        let roundedSize = Math.round(fileSizeInMegabytes);
        if (result) {
          this.model.fileSize = roundedSize + 'KB';
        } else {
          this.model.fileSize = roundedSize + 'MB';
        }
      }
    }
    // Note: `fileChange` is NOT emitted here. The raw file still needs to be cropped
    // (dialog opens right after this returns); emitting now would fire consumers (e.g.
    // DNI auto-extraction) before the user has even seen the crop dialog. See `closeModal()`.
    return true;
  }

  isSizeInMegabytes(size: number, unit: string) {
    if (unit.toLowerCase() === 'kb') {
      size = size / 1024;
    }
    return size <= 1;
  }

  async imageCropped(event: ImageCroppedEvent) {
    // `<image-cropper>` defaults its `[output]` input to 'blob' (we never bind it), which means
    // `event.blob` is the real cropped image data and `event.base64` is left `null`/`undefined`.
    // The previous code unconditionally did `fetch(event.base64)` — with `base64` undefined,
    // that resolves to fetching the literal relative URL "undefined", which (thanks to this
    // being an SPA with an index.html fallback rewrite) silently "succeeds" by returning the
    // app's own HTML shell as the "image" bytes. That corrupt HTML-labeled-as-PNG file is what
    // was failing the backend's magic-byte validation ("Could not extract data from ID document")
    // and also why the local preview never rendered (a browser can't display HTML as an <img>).
    // Read directly from `event.blob` (falling back to `event.base64` only if a caller ever
    // explicitly binds `[output]="'base64'"` on the cropper in the future).
    const blob = event.blob ?? (event.base64 ? await fetch(event.base64).then((r) => r.blob()) : null);
    if (!blob) {
      return;
    }
    // The cropper is always configured with `format="png"` (see template), so its output is
    // ALWAYS PNG bytes regardless of the original file's extension/type. Reusing the original
    // `lastFileName` here (e.g. "photo.jpg") would produce a `File` whose name/extension lies
    // about its actual content — the backend's magic-byte validation
    // (`ocr-extraction.service.ts`'s `validateMagicBytes`) correctly rejects that mismatch,
    // surfacing as "Could not extract data from ID document". Force a `.png` name + matching
    // MIME type so what's declared always matches what's actually inside the file.
    const pngFileName = (this.lastFileName || 'image').replace(/\.[^./\\]+$/, '') + '.png';
    const file = new File([blob], pngFileName, { type: 'image/png' });
    // Stash the file + its object URL, but do NOT assign `localUrl` yet. `(imageCropped)` fires
    // automatically once on load (autoCrop) WITHIN the same tick that opened the dialog and
    // rendered this widget; assigning `localUrl` synchronously here would flip the main widget's
    // `@if ((url || localUrl) && !onlyImg)` from false→true mid-change-detection → NG0100
    // ("branch index -1 → shown", reported at upload-image.component.html:4). `localUrl` is set
    // only on confirm (see `closeModal()`), which runs from a clean user-click tick.
    this.pendingCroppedFile = file;
    this.pendingCroppedUrl = window.URL.createObjectURL(file);
  }

  closeModal() {
    const file = this.pendingCroppedFile;
    const objectUrl = this.pendingCroppedUrl;
    this.pendingCroppedFile = null;
    this.pendingCroppedUrl = null;
    // Reflect the confirmed crop in the main widget. Safe to assign synchronously: `closeModal()`
    // runs from the "Save" button's click handler (its own event tick), so Angular runs change
    // detection AFTER this returns — no mid-check mutation, unlike the cropper's autoCrop emit.
    if (objectUrl) {
      this.localUrl = objectUrl;
    }
    // Emit AFTER the dialog has fully closed rather than synchronously here. Consumers
    // (e.g. `edit-profile.component.ts`'s `onDniFrontChange()`) react to `fileChange` by
    // kicking off work synchronously (setting `extractingDni = true`, which renders a
    // loading hint in the dialog underneath). If we emitted before `dialogReference.close()`,
    // that hint would start rendering WHILE this crop dialog is still visible and animating
    // out (~200ms), making it look like the loader is stuck "behind" the still-closing popup.
    // Waiting for `afterClosed()` guarantees the popup is fully gone before any consumer-driven
    // UI change appears.
    this.dialogReference.afterClosed().subscribe(() => this.fileChange.emit(file));
    this.dialogReference.close(true);
  }

  async downloadImg() {
    await this.downloadFile(this.url ?? this.localUrl, this.model.fileName);
  }

  async downloadFile(url: string, fileName: string) {
    if (!url || !fileName) {
      return;
    }

    try {
      let blob: Blob;

      if (url.startsWith('blob:')) {
        const response = await fetch(url);
        blob = await response.blob();
      } else {
        const response = await fetch(url);
        if (!response.ok){
          return;
        }
        blob = await response.blob();
      }

      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(a);
    } catch (error) {}
  }
}
